/**
 * Reusable Usage Tab Component
 * 
 * Displays usage list filtered by promoId or campaignId:
 * - Table listing all usages
 * - Pagination
 * - Request cancellation support
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, Alert } from 'react-bootstrap';
import { getPromoUsages } from '../../../api/promos';
import { UsageListParams } from '../../../api/promos/types';
import UsageTable from '../../../pages/Promos/Promos/PromoDetail/components/UsageTable';
import ShimmerLoader from '../../../Common/ShimmerLoader';
import Pagination from '../../../pages/ShopServices/Orders/components/Pagination';

interface UsageTabProps {
  promoId?: string;
  campaignId?: string;
  emptyMessage?: string;
}

const UsageTab: React.FC<UsageTabProps> = ({ 
  promoId, 
  campaignId,
  emptyMessage = 'No usage data found.'
}) => {
  const [usages, setUsages] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  
  // Filters state - using filters.pageNum as single source of truth
  const [filters, setFilters] = useState<UsageListParams>({
    ...(promoId ? { promoId } : {}),
    ...(campaignId ? { campaignId } : {}),
    pageNum: 1,
    pageSize: 20,
  });
  
  // Derived value for currentPage (using filters.pageNum as source of truth)
  const currentPage = filters.pageNum || 1;
  
  // AbortController ref for request cancellation
  const abortControllerRef = useRef<AbortController | null>(null);
  
  // Fetch usage data
  const fetchUsages = useCallback(async () => {
    // Cancel any ongoing request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    // Create new AbortController for this request
    const abortController = new AbortController();
    abortControllerRef.current = abortController;
    
    try {
      setLoading(true);
      setError(null);
      
      const response = await getPromoUsages({
        ...filters,
        ...(promoId ? { promoId } : {}),
        ...(campaignId ? { campaignId } : {}),
      });
      
      // Check if request was aborted
      if (abortController.signal.aborted) {
        return;
      }
      
      setUsages(response.results || []);
      setTotalCount(response.totalCount || 0);
    } catch (err: any) {
      // Don't set error if request was aborted
      if (abortController.signal.aborted || err.name === 'AbortError' || err.code === 'ERR_CANCELED') {
        return;
      }
      setError(err.message || 'Failed to load usage data. Please try again.');
    } finally {
      // Only update loading state if request wasn't aborted
      if (!abortController.signal.aborted) {
        setLoading(false);
      }
    }
  }, [filters, promoId, campaignId]);
  
  // Load usages on mount and filter changes
  useEffect(() => {
    fetchUsages();
    
    // Cleanup: cancel request if component unmounts or filters change
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [fetchUsages]);
  
  // Handle pagination
  const handlePageChange = useCallback((page: number) => {
    setFilters((prev: UsageListParams) => ({
      ...prev,
      pageNum: page,
    }));
  }, []);
  
  const handlePageSizeChange = useCallback((size: number) => {
    setFilters((prev: UsageListParams) => ({
      ...prev,
      pageSize: size,
      pageNum: 1,
    }));
  }, []);
  
  return (
    <>
      <Card>
        <Card.Body style={{ padding: '24px' }}>
          {/* Error Alert */}
          {error && (
            <Alert variant="danger" onClose={() => setError(null)} dismissible className="mb-3">
              {error}
            </Alert>
          )}
          
          {/* Table */}
          {loading ? (
            <ShimmerLoader type="table" rows={5} />
          ) : usages.length > 0 ? (
            <UsageTable
              usages={usages}
              totalCount={totalCount}
              currentPage={currentPage}
              pageSize={filters.pageSize || 20}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
            />
          ) : (
            <div className="text-center py-5">
              <p className="text-muted mb-0">{emptyMessage}</p>
            </div>
          )}
        </Card.Body>
      </Card>
      
      {/* Pagination - Outside Card to match Audit Logs listing page structure */}
      {!loading && Math.ceil(totalCount / (filters.pageSize || 20)) > 1 && (
        <Pagination
          currentPage={currentPage}
          totalPages={Math.ceil(totalCount / (filters.pageSize || 20))}
          totalCount={totalCount}
          pageSize={filters.pageSize || 20}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
        />
      )}
    </>
  );
};

export default UsageTab;
